import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { TenantProvider } from './context/TenantContext';
import { Toaster } from './components/ui/sonner';
import './i18n';

// Layouts
import OperatorLayout from './layouts/OperatorLayout';
import ClientLayout from './layouts/ClientLayout';

// Landing & Auth Pages
import Landing from './pages/Landing';
import Login from './pages/Login';
import Register from './pages/Register';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';

// Operator Pages
import OperatorDashboard from './pages/operator/Dashboard';
import Tenants from './pages/operator/Tenants';
import Users from './pages/operator/Users';
import Packages from './pages/operator/Packages';
import Templates from './pages/operator/Templates';
import AddOns from './pages/operator/AddOns';
import TasksKanban from './pages/operator/TasksKanban';
import Documents from './pages/operator/Documents';
import BillingAdmin from './pages/operator/BillingAdmin';
import Integrations from './pages/operator/Integrations';
import AuditLogs from './pages/operator/AuditLogs';
import SmokeTest from './pages/operator/SmokeTest';
import OnboardingReview from './pages/operator/OnboardingReview';
import Assignments from './pages/operator/Assignments';
import Activity from './pages/operator/Activity';
import Workspace from './pages/operator/Workspace';
import OperatorFacturacion from './pages/operator/Facturacion';
import OperatorGastos from './pages/operator/Gastos';
import OperatorCierreMensual from './pages/operator/CierreMensual';
import OperatorModelosImpuestos from './pages/operator/ModelosImpuestos';
import CatalogClients from './pages/operator/CatalogClients';
import CatalogVendors from './pages/operator/CatalogVendors';
import CatalogTaxModels from './pages/operator/CatalogTaxModels';
import OperatorHoldedExports from './pages/operator/HoldedExports';
import OperatorChatInbox from './pages/operator/ChatInbox';

// Client Pages
import ClientDashboard from './pages/client/Dashboard';
import Requests from './pages/client/Requests';
import ClientTasks from './pages/client/Tasks';
import ClientDocuments from './pages/client/Documents';
import ClientBilling from './pages/client/Billing';
import BillingSuccess from './pages/client/BillingSuccess';
import RequestWizard from './pages/client/RequestWizard';
import ClientInvoices from './pages/client/Invoices';
import ClientExpenses from './pages/client/Expenses';
import ClientExports from './pages/client/Exports';
import OnboardingCompleted from './pages/client/OnboardingCompleted';
import ClientCierreMensual from './pages/client/CierreMensual';
import ClientChat from './pages/client/Chat';
import ClientTemplates from './pages/client/Templates';
import ClientImports from './pages/client/Imports';
import ClientImportBatchDetail from './pages/client/ImportBatchDetail';
import ClientHoldedIntegration from './pages/client/HoldedIntegration';
import ClientReports from './pages/client/Reports';
import ClientHoldedExports from './pages/client/HoldedExports';
import ClientUsers from './pages/client/Users';

// Onboarding
import OnboardingWizard from './pages/onboarding/OnboardingWizard';

const PrivateRoute = ({ children, requireOperator, requireGlobalAdmin }) => {
  const { user, loading, isOperator, isGlobalAdmin } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Cargando...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" />;
  }

  if (requireGlobalAdmin && !isGlobalAdmin()) {
    return <Navigate to="/operator/dashboard" />;
  }

  if (requireOperator && !isOperator()) {
    return <Navigate to="/client/dashboard" />;
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      <Route path="/portal/onboarding/completado" element={<Navigate to="/client/onboarding/completado" replace />} />
      <Route path="/portal/*" element={<Navigate to="/client/dashboard" replace />} />
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Operator Routes */}
      <Route
        path="/operator"
        element={
          <PrivateRoute requireOperator>
            <OperatorLayout />
          </PrivateRoute>
        }
      >
        <Route path="dashboard" element={<OperatorDashboard />} />
        <Route path="workspace" element={<Workspace />} />
        <Route path="facturacion" element={<OperatorFacturacion />} />
        <Route path="gastos" element={<OperatorGastos />} />
        <Route path="cierre-mensual" element={<OperatorCierreMensual />} />
        <Route path="impuestos" element={<OperatorModelosImpuestos />} />
        <Route path="catalogos/clientes" element={<CatalogClients />} />
        <Route path="catalogos/proveedores" element={<CatalogVendors />} />
        <Route path="catalogos/modelos" element={<CatalogTaxModels />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="addons" element={<AddOns />} />
        <Route path="tasks" element={<TasksKanban />} />
        <Route path="documents" element={<Documents />} />
        <Route path="billing" element={<BillingAdmin />} />
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="smoke-test" element={<SmokeTest />} />
        <Route path="holded-exports" element={<OperatorHoldedExports />} />
        <Route path="chat" element={<OperatorChatInbox />} />
      </Route>

      {/* Global Admin Routes */}
      <Route
        path="/global-admin"
        element={
          <PrivateRoute requireGlobalAdmin>
            <OperatorLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/global-admin/dashboard" replace />} />
        <Route path="dashboard" element={<OperatorDashboard />} />
        <Route path="workspace" element={<Workspace />} />
        <Route path="facturacion" element={<OperatorFacturacion />} />
        <Route path="gastos" element={<OperatorGastos />} />
        <Route path="cierre-mensual" element={<OperatorCierreMensual />} />
        <Route path="impuestos" element={<OperatorModelosImpuestos />} />
        <Route path="catalogos/clientes" element={<CatalogClients />} />
        <Route path="catalogos/proveedores" element={<CatalogVendors />} />
        <Route path="catalogos/modelos" element={<CatalogTaxModels />} />
        <Route path="tenants" element={<Tenants />} />
        <Route path="users" element={<Users />} />
        <Route path="assignments" element={<Assignments />} />
        <Route path="activity" element={<Activity />} />
        <Route path="packages" element={<Packages />} />
        <Route path="templates" element={<Templates />} />
        <Route path="addons" element={<AddOns />} />
        <Route path="tasks" element={<TasksKanban />} />
        <Route path="documents" element={<Documents />} />
        <Route path="billing" element={<BillingAdmin />} />
        <Route path="integrations" element={<Integrations />} />
        <Route path="audit-logs" element={<AuditLogs />} />
        <Route path="smoke-test" element={<SmokeTest />} />
        <Route path="holded-exports" element={<OperatorHoldedExports />} />
        <Route path="chat" element={<OperatorChatInbox />} />
      </Route>

      <Route
        path="/ops"
        element={
          <PrivateRoute requireGlobalAdmin>
            <OperatorLayout />
          </PrivateRoute>
        }
      >
        <Route path="onboarding" element={<OnboardingReview />} />
      </Route>

      {/* Client Routes */}
      <Route
        path="/client"
        element={
          <PrivateRoute>
            <ClientLayout />
          </PrivateRoute>
        }
      >
        <Route path="dashboard" element={<ClientDashboard />} />
        <Route path="requests" element={<Requests />} />
        <Route path="requests/new" element={<RequestWizard />} />
        <Route path="tasks" element={<ClientTasks />} />
        <Route path="documents" element={<ClientDocuments />} />
        <Route path="invoices" element={<ClientInvoices />} />
        <Route path="expenses" element={<ClientExpenses />} />
        <Route path="exports" element={<ClientExports />} />
        <Route path="templates" element={<ClientTemplates />} />
        <Route path="imports" element={<ClientImports />} />
        <Route path="imports/:id" element={<ClientImportBatchDetail />} />
        <Route path="reports" element={<ClientReports />} />
        <Route path="users" element={<ClientUsers />} />
        <Route path="integrations/holded" element={<ClientHoldedIntegration />} />
        <Route path="cierre-mensual" element={<ClientCierreMensual />} />
        <Route path="billing" element={<ClientBilling />} />
        <Route path="billing/success" element={<BillingSuccess />} />
        <Route path="onboarding" element={<OnboardingWizard />} />
        <Route path="onboarding/completado" element={<OnboardingCompleted />} />
        <Route path="chat" element={<ClientChat />} />
        <Route path="holded-exports" element={<ClientHoldedExports />} />
      </Route>

      {/* /portal removed; use /client routes */}
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <TenantProvider>
          <AppRoutes />
          <Toaster position="top-right" />
        </TenantProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
